import React from 'react';
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from '@mui/material/styles';
import { Container } from '@mui/material';
import {
  useInitFirestore,
  useFirebaseApp,
  AuthProvider,
  AnalyticsProvider,
  FirestoreProvider,
  FunctionsProvider,
  StorageProvider,
  RemoteConfigProvider,
  useInitRemoteConfig,
} from 'reactfire';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import {
  initializeFirestore, persistentLocalCache, persistentSingleTabManager,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config';
import CssBaseline from '@mui/material/CssBaseline';
import LoadingPage from 'components/LoadingPage';
import Home from 'components/Home';

const theme = createTheme({
  palette: {
    primary: {
      main: '#437A89',
      contrastText: 'white',
    },
    secondary: {
      main: '#73a9b9',
      contrastText: 'white',
    },
    text: {
      primary: '#3f4d5a',
    },
    info: {
      main: '#437A89',
      contrastText: 'white',
    },
    ourGreen: {
      main: '#99BD5B',
      contrastText: 'white',
    },
    ourOrange: {
      main: '#f4ad45',
      contrastText: 'white',
    },
    gold: {
      main: '#FFD700',
      contrastText: 'white',
    },
    darkgray: {
      main: '#0000008a',
      contrastText: 'white',
    },
    mediumgray: {
      main: '#8585858a',
      contrastText: 'white',
    },
    gray: {
      main: '#d8d8d8',
      contrastText: 'white',
    },
  },
});

export default function AllReactFireProviders() {
  const app = useFirebaseApp();
  const auth = getAuth(app);

  if (process.env.NODE_ENV === 'development') {
    connectAuthEmulator(auth, 'http://localhost:9099');
  }

  return (
    <AnalyticsProvider sdk={getAnalytics(app)}>
      <AuthProvider sdk={auth}>
        <DigiFirestoreProvider />
      </AuthProvider>
    </AnalyticsProvider>
  );
}

function DigiFirestoreProvider() {
  const app = useFirebaseApp();
  const functionsInstance = getFunctions(app);
  const storageInstance = getStorage(app);

  const { status: firestoreStatus, data: firestoreInstance } = useInitFirestore(async (firebaseApp) => {
    const baseSettings = {
      localCache: persistentLocalCache({
        tabManager: persistentSingleTabManager({
          // forceOwnership for web worker
          // eslint-disable-next-line no-undef
          forceOwnership: !globalThis.localStorage,
        }),
      }),
    };
    const savedSetting = localStorage.getItem('forceLongPolling');
    const forceLongPolling = savedSetting === 'true';
    if (forceLongPolling) {
      baseSettings.experimentalForceLongPolling = true;
    } else {
      baseSettings.experimentalAutoDetectLongPolling = true;
    }
    const fsSettings = (process.env.NODE_ENV === 'development')
      ? {
        ...baseSettings,
        host: 'localhost:4401',
        ssl: false,
      } : baseSettings;
    const db = initializeFirestore(firebaseApp, fsSettings);
    return db;
  });

  const { status: remoteConfigStatus, data: remoteConfigInstance } = useInitRemoteConfig(async (firebaseApp) => {
    const remoteConfig = getRemoteConfig(firebaseApp);
    remoteConfig.settings = {
      minimumFetchIntervalMillis: 600000,
      fetchTimeoutMillis: 600000,
    };

    try {
      await fetchAndActivate(remoteConfig);
    } catch (e) {
      console.warn('Remote config failed to be activated');
    }
    return remoteConfig;
  });

  if (firestoreStatus === 'loading' || remoteConfigStatus === 'loading') {
    return (
      <LoadingPage />
    );
  }

  return (
    <FirestoreProvider sdk={firestoreInstance}>
      <AllFirebaseProviders
        functionsInstance={functionsInstance}
        storageInstance={storageInstance}
        remoteConfigInstance={remoteConfigInstance}
      />
    </FirestoreProvider>
  );
}

function AllFirebaseProviders(props) {
  const { functionsInstance, storageInstance, remoteConfigInstance } = props;

  // We are initializing emulator earlier due to bug mentionned in:
  // https://github.com/FirebaseExtended/reactfire/issues/443
  if (process.env.NODE_ENV === 'development') {
    connectFunctionsEmulator(functionsInstance, 'localhost', 5001);
    connectStorageEmulator(storageInstance, 'localhost', 9199);
  }

  return (
    <FunctionsProvider sdk={functionsInstance}>
      <StorageProvider sdk={storageInstance}>
        <RemoteConfigProvider sdk={remoteConfigInstance}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <Container>
                <CssBaseline />
                <Home />
              </Container>
            </ThemeProvider>
          </StyledEngineProvider>
        </RemoteConfigProvider>
      </StorageProvider>
    </FunctionsProvider>
  );
}
